// What the guest 3D map needs from a saved room design, kept apart from scene-geometry so that the
// original map's code path stays untouched. Pure module: relative imports only.
import { computeLightingHints, type LightingHints } from '../room-3d/lighting-hints.ts';
import { type ObjectFloor, objectWorldTransform } from '../room-3d/objects/object-geometry.ts';
import { ROOM_OBJECT_KINDS, type RoomObjectKind } from '../room-3d/objects/object-kinds.ts';
import { isBehindBackWall, objectBaseMm, objectFloorAt } from '../room-3d/objects/object-placement.ts';
import { normalizeObjectProfile, normalizeRoomGeometry } from '../room-3d/outline/room-geometry.ts';
import type { ObjectProfile, RoomGeometry } from '../room-3d/outline/types.ts';
import { normalizeRoomDesign, type RoomDesign, type TierPlatformSpec } from '../room-3d/room-design.ts';
import { boxExtent, type Extent, unionExtents } from '../room-3d/room-fit.ts';
import { resolveSeatForms } from '../room-3d/seat-forms.ts';
import { seatLookOf } from '../room-3d/seat-styles.ts';
import { deriveTiers, type TierModel } from '../room-3d/tiers.ts';
import type { RoomObject, Seat } from '../seat-map/typedefs.ts';

import { GROUND_Y, MM, type SceneLayoutOptions, type SeatNode, type Vec3 } from './scene-geometry.ts';

/**
 * The design saved on the room, or `null` for a room the studio never touched. A room without one
 * renders exactly as before; a present but partly unreadable one is repaired by `normalizeRoomDesign`.
 */
export function roomDesignFromDto(dto: { design?: unknown } | undefined): RoomDesign | null {
    const raw = dto?.design;
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? normalizeRoomDesign(raw) : null;
}

/** The untrusted geometry value of a room: `CinemaRoom.geometry`, or the shape embed of its DTO. Same object on every call. */
export function rawRoomGeometry(room: {
    geometry?: unknown;
    dto?: { _embedded?: { cinemaRoomShape?: unknown } };
}): unknown {
    const shape = room.dto?._embedded?.cinemaRoomShape as { geometry?: unknown } | undefined;
    return room.geometry ?? shape?.geometry;
}

/** A room's geometry, normalized; null for a plain rectangle (nothing stored, or nothing usable). */
export function geometryFromRaw(raw: unknown): RoomGeometry | null {
    if (raw === undefined || raw === null) return null;
    const geometry = normalizeRoomGeometry(raw);
    return geometry.outline || geometry.levels.length > 0 ? geometry : null;
}

/** `geometryFromRaw(rawRoomGeometry(room))`; components memoize on the raw value instead, to keep the result stable. */
export function roomGeometryOf(room: Parameters<typeof rawRoomGeometry>[0]): RoomGeometry | null {
    return geometryFromRaw(rawRoomGeometry(room));
}

/** What the form resolver needs of a guest seat. */
export type FormSeat = Pick<Seat, 'id'> & { category?: { id: number | string } | null };

export function layoutOptionsForDesign(
    design: RoomDesign | null,
    room?: { width: number; height: number; geometry?: RoomGeometry | null },
    seats: readonly FormSeat[] = []
): SceneLayoutOptions {
    if (!design) return {};
    const forms = resolveSeatForms(
        design,
        seats.map((seat) => ({ id: seat.id, categoryId: seat.category?.id })),
        (seat) => seat.id
    );
    return {
        seatStyle: design.seatStyle,
        screenAboveFloor: true,
        room,
        seatLook: seatLookOf(design),
        seatForm: forms.room,
        seatForms: forms.bySeat
    };
}

export type GuestRoomObject = {
    id: RoomObject['id'];
    kind: RoomObjectKind;
    position: Vec3;
    rotationY: number;
    width: number;
    depth: number;
    height: number;
    /** A curved outline (round stage, curved barrier...) or null for a plain box. */
    profile: ObjectProfile | null;
    /** A railing's floor along its path, relative to its base (CIN-15200): it steps with the tiers. */
    floorAt?: ObjectFloor;
};

const isKind = (type: string): type is RoomObjectKind => (ROOM_OBJECT_KINDS as readonly string[]).includes(type);

/**
 * Every room object except the screen, which the map draws itself with the showing's backdrop.
 * Types the studio does not know are kept as the neutral `other` box, like in the studio.
 */
export function guestRoomObjects(roomObjects: RoomObject[]): GuestRoomObject[] {
    const out: GuestRoomObject[] = [];
    for (const o of roomObjects) {
        if (!o.type || o.type === 'screen' || o.width <= 0 || o.depth <= 0 || o.height <= 0) continue;
        const transform = o.dto._embedded?.transform;
        const { position, rotationY } = objectWorldTransform({
            x: transform?.positionX ?? 0,
            y: transform?.positionY ?? 0,
            z: transform?.positionZ ?? 0,
            rotation: transform?.rotationZ ?? 0
        });
        out.push({
            id: o.id,
            kind: isKind(o.type) ? o.type : 'other',
            position,
            rotationY,
            width: o.width,
            depth: o.depth,
            height: o.height,
            profile: normalizeObjectProfile(o.dto._embedded?.shape?.profile)
        });
    }
    return out;
}

/**
 * The objects standing on the floor the rows stand on: everything that is not mounted on purpose takes its
 * base from the tier under its footprint (see `objectBaseMm`), so nothing hovers over a platform or is buried
 * in one. `position.y` is the only thing that changes.
 */
export function groundGuestObjects(
    objects: GuestRoomObject[],
    tiers: TierModel,
    geometry?: RoomGeometry | null
): GuestRoomObject[] {
    return objects.map((o) => {
        const placement = {
            x: o.position.x * MM,
            y: -o.position.z * MM,
            z: (o.position.y - GROUND_Y) * MM,
            width: o.width,
            depth: o.depth,
            rotation: (-o.rotationY * 180) / Math.PI
        };
        const base = objectBaseMm(o.kind, placement, tiers, geometry);
        const floorAt = objectFloorAt(o.kind, placement, base, tiers, geometry);
        return { ...o, position: { ...o.position, y: GROUND_Y + base / MM }, ...(floorAt ? { floorAt } : {}) };
    });
}

/** Whether a guest object stands behind the back wall of a room `depthMm` deep (a booth projector, not room content). */
export function isBehindGuestWall(object: GuestRoomObject, depthMm: number, geometry?: RoomGeometry | null): boolean {
    return isBehindBackWall({ x: object.position.x * MM, y: -object.position.z * MM }, depthMm, geometry?.outline);
}

/** Rows and aisles for the design's step and aisle lights, derived from where the seats are. */
export function lightingHintsFor(seats: SeatNode[]): LightingHints {
    return computeLightingHints(seats);
}

/** Where the seats and objects stand in the world, so the shell can grow around whatever pokes out of the room. */
export function guestContentExtent(seats: SeatNode[], objects: GuestRoomObject[]): Extent | null {
    return unionExtents([
        ...seats.map((seat) => boxExtent(seat.position, seat.size, seat.rotationY)),
        ...objects.map((o) =>
            boxExtent(o.position, { x: o.width / 1000, y: o.height / 1000, z: o.depth / 1000 }, o.rotationY)
        )
    ]);
}

/**
 * The stepped floor under the rows of a designed room; empty when the rows are level. The design's platform entries
 * (`tiers.platforms`) name rows, so they need `rowNames` (seat id to row name).
 */
export function guestTiersFor(
    seats: SeatNode[],
    room: { width: number; depth: number; geometry?: RoomGeometry | null },
    platforms: readonly TierPlatformSpec[] = [],
    rowNames?: ReadonlyMap<SeatNode['seatId'], string>,
    autoStairs = true
): TierModel {
    return deriveTiers(
        seats.map((seat) => ({
            x: seat.position.x * 1000,
            y: -seat.position.z * 1000,
            z: (seat.position.y - GROUND_Y) * 1000,
            width: seat.size.x * 1000,
            depth: seat.size.z * 1000,
            rotation: (-seat.rotationY * 180) / Math.PI,
            rowName: rowNames?.get(seat.seatId)
        })),
        room,
        platforms,
        autoStairs
    );
}
